import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import QueryService from "services/QueryService";
import { makeStyles } from "@mui/styles";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  Save as SaveIcon,
  AddCircle
} from "@mui/icons-material";

import { default as _cloneDeep } from "lodash/cloneDeep";
import { default as _head } from "lodash/head";
import { default as _set } from "lodash/set";
import { default as _isEmpty } from "lodash/isEmpty";

import ISXUtils, {
  ErrorContext,
  TagRoleClipboardContextProvider,
  TagRoleClipboardContext,
} from "services/Utils";
import {
  getTagInfoForStack,
  getSortedTagNamesFromInfo,
} from "components/ComboBox/tags/Utils";
import { getRole } from "services/RolesService";
import ComboBox from "components/ComboBox";
import TagConfig from "components/TagConfig";

import { useSelector } from "react-redux"; //for orgs list that contains predefined CVT exprs

const useStyles = makeStyles((theme) => ({
  tagsActionsHeader: {
    paddingLeft: 3,
    marginBottom: 12,
  },
  actionButton: {
    marginLeft: 3,
    marginRight: 3,
  },
  tagDirtyToast: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
}));

const TagRoleClipboard = ({ stack }) => {
  const roleContext = useContext(TagRoleClipboardContext);
  const { tagId, roleId, contents } = roleContext.clipboard;
  if (contents) {
    return (
      <Typography color="primary" style={{ fontWeight: 700 }}>
        {`${ISXUtils.getTagName(tagId, stack?.data_config) || tagId} : ${getRole(roleId)?.name ?? roleId
          } stored in clipboard`}
      </Typography>
    );
  } else {
    return null;
  }
};

function jsonParseIgnoreErr(t, d) {
  try {
    return JSON.parse(t);
  } catch (e) {
    console.log("Warning: JSON Parse error ignored", e);
    return d;
  }
}

const StackTagsConfig = ({
  stack,
  updateItem,
  setTitle,
  dirty,
  setDirty,
  readOnly,
}) => {
  const [tagProperties, setTagProperties] = useState({});
  const [newTag, setNewTag] = useState(false);
  const [newTagId, setNewTagId] = useState("");    
  const [tab, setTab] = useState(0);
  const [tagIds, setTagIds] = useState([]);
  const [dirtyProperties, setDirtyProperties] = useState({});
  const [inputVal, setInputVal] = React.useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [saveWarning, setSaveWarning] = useState(0);
  const [saveToastOpen, setSaveToastOpen] = useState(false);
  const [dirtyToastOpen, setDirtyToastOpen] = useState(false);
  const lastStackId = useRef(null);

  const errorContext = useContext(ErrorContext);

  const classes = useStyles();
  const orgs = useSelector((state) => state.isx.orgs);
  // Predefined CVT expressions are collected from all known orgs
  const predefined = useMemo(() => orgs ? Object.values(orgs).reduce((a,org) => org.predefined_cvts ?
                                                                     [...a, ...jsonParseIgnoreErr(org.predefined_cvts,[])] : a,  []) : [],
                             [orgs]);
  
  const getTagPropertiesFromConfig = useCallback(
    async (tagIds) => {
      const props = await tagIds.reduce((acc, tid) => {
        acc[tid] = _cloneDeep(stack.data_config?.[tid] ?? {});
        return acc;
      }, {});
      const derivedProps = await QueryService.listTagDerived(stack.guuid);
      derivedProps.forEach((dp) => {
        const tid = dp.dsid.slice(dp.dsid.indexOf("_")+1);
        if(props.hasOwnProperty(tid)) props[tid]["$derived"] = dp.expr;
      });
      return props;
    }, [stack.data_config, stack.guuid]
  );

  useEffect(() => {
    const suffix =
      selectedTags.length > 0
        ? ` (${selectedTags.length} of ${tagIds.length})`
        : "";
    setTitle(`Manage Tags${suffix}`);
  }, [selectedTags.length, setTitle, stack, tagIds.length]);

  useEffect(() => {
    if (stack && stack.guuid !== lastStackId.current) {
      lastStackId.current = stack.guuid;
      const tagIds = (stack.data_config?.dsitems ?? []).sort();
      // parallel arrays
      setTagIds(tagIds);
      getTagPropertiesFromConfig(tagIds).then((tagProperties) => {
        setTagProperties(tagProperties);
      });
    }
  }, [stack, getTagPropertiesFromConfig]);

  const handleTagPropsChange = (tagId, tagProps) => {
    if(tagId && Object.keys(tagProps).any((prop) => prop.startsWith("states") || prop === '$derived')) {
      setSaveWarning(1);
    }
    setTagProperties((tagProperties) => {
      return {
        ...tagProperties,
        [tagId]: { ...tagProperties[tagId], ...tagProps },
      };
    });
    setDirtyProperties((current) => {
      const dirtyProps = Object.keys(tagProps).reduce((acc, p) => {
        acc[p] = true;
        return acc;
      }, {});
      return { ...current, [tagId]: { ...current[tagId], ...dirtyProps } };
    });
    setDirty(true);
  };

  const handleTagPropChange = (tagId, prop, value) => {
    //warn if not a brand new property 
    if(tagId && (prop.startsWith("states") || prop === '$derived')) setSaveWarning(1);
    //hack here for CVT/derive calculation: special prop "$derived" represents updated derived calculation
    const propPath = prop.split(".");
    const headProp = _head(propPath);
    setTagProperties((tagProperties) => {
      return {
        ...tagProperties,
        [tagId]: _set({ ...tagProperties[tagId] }, propPath, value),
      };
    });
    setDirtyProperties((current) => {
      return { ...current, [tagId]: { ...current[tagId], [headProp]: true } };
    });
    setDirty(true);
  };

  async function saveTagPropertiesToDB() {
    const derivedUpdates = {} //see hack
    const update = Object.entries(dirtyProperties).reduce(
      (acc, [tid, props]) => {
        const tagIdEscaped = ISXUtils.escapeTagPeriods(tid === '' ? newTagId : tid);
        return Object.entries(props).reduce((acci, [prop, flag]) => {
          if (flag) {
            const val = (tagProperties[tid] ?? {})[prop];
            if(prop === "$derived") { //special case - bit of a hack to reuse code
              derivedUpdates[tid] = val;
            } else {
              const key = `data_config.${tagIdEscaped}.${prop}`;
              acci[key] = val;
            }
          }
          return acci;
        }, acc);
      },
      {}
    );
    if(newTag) {
      //todo fix error handling
      if(! /^[a-z][a-z0-9_]*$/.test(newTagId) || newTagId.length < 3) throw new Error("tag id must be lowercase string of len > 2");
      if(tagProperties[newTagId]) throw new Error("tag id already exists");
      update["data_config.dsitems"] = [...stack.data_config?.dsitems ?? [], newTagId];
    }
    if (!_isEmpty(update)) {
      await updateItem(stack.guuid, update);
    }
    Object.entries(derivedUpdates).map( async ([tid,val]) => {
      console.log("UPDATE DERIVED", stack.guuid, tid, typeof(val), val);
      await QueryService.setTagDerived(stack.guuid, tid, val);
    });
    setDirtyProperties({});
    setDirty(false);
    setSaveToastOpen(true);
    if(newTag) {
       //move to actual tag id
      tagProperties[newTag] = tagProperties[''];
      delete tagProperties[''];
      reset(); //todo - refresh tag properties b/c we changed data_config.dsitems
    }
  }

  // create tag named inputVal
  async function createNewTag() {
    setNewTag(true);
    setDirty(true);
    //for now add all interesting roles (later we'll need to provide for editing which to include)
    handleTagPropChange('', 'roles', [
      '597b1e27-964f-46b4-a88d-971e0e19553d',
      'a2fe6658-ebd9-47ac-ad9a-9ea24720b928',
      '1d560741-a619-42cc-9d30-f13bc9e03930',
      'cb28cf96-094c-4b34-bfbd-c14360de000a'
                                     ]);
    handleTagPropChange('', 'isx_tag_user_writable', true);

  }

  const reset = async () => {
    const lastTagProperties = await getTagPropertiesFromConfig(tagIds);
    setTagProperties(lastTagProperties);
    setNewTag(false);
    setNewTagId("");
    setDirtyProperties({});
    setDirty(false);
    setSaveWarning(0);
  };

  const closeSaveToast = () => {
    setSaveToastOpen(false);
  };

  const closeDirtyToast = () => {
    setDirtyToastOpen(false);
  };

  const tagInfo = useMemo(
    () => (stack ? getTagInfoForStack(stack, null, false) : {}),
    [stack]
  );

  const tagNames = useMemo(() => getSortedTagNamesFromInfo(tagInfo), [tagInfo]);

  return (
    <TagRoleClipboardContextProvider>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <div>
          <div className={classes.tagsActionsHeader}>
            <Grid container spacing={1}>
              <Grid item md={6} xs={12}>
                <div style={{ position: "relative" }}>
                  <ComboBox
                    value={selectedTags}
                    inputValue={inputVal}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") {
                        setInputVal(value);
                      }
                    }}
                    onClose={() => setInputVal("")}
                    options={tagNames}
                    onChange={(_, tags) => {
                      if (tags) {
                        setSelectedTags(tags);
                        if (tab >= tags.length) {
                          setTab(tags.length > 0 ? tags.length - 1 : 0);
                        }
                      }
                    }}
                    getOptionLabel={(attr) =>
                      attr ? tagInfo[attr]?.name ?? attr : ""
                    }
                    renderOption={(props, option, { selected }) => {
                      return <Box component="li" key={option} {...props}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          style={{
                            paddingRight: "0.25rem",
                            paddingLeft: 0,
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          checked={selected}
                        />
                        {option ? tagInfo[option]?.name ?? option : ""}
                      </Box>
                    }}
                    label="Add tags to view/manage"
                    disableClearable
                    clearOnBlur={false}
                    defaultValue={tagIds.length === 1 ? tagIds : []}
                    multiple
                    limitTags={5}
                    getOptionDisabled={() => selectedTags.length >= 12}
                    disableCloseOnSelect
                    disabled={dirty}
                  />
                  {dirty && (
                    <div
                      className={classes.tagDirtyToast}
                      onClick={() => setDirtyToastOpen(true)}
                    />
                  )}
                  <Snackbar
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    open={dirtyToastOpen}
                    autoHideDuration={2000}
                    onClose={closeDirtyToast}
                    message="Please save/cancel outstanding changes first"
                    style={{ position: "absolute" }}
                  />
                </div>
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                style={{
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                {dirty && (
                  <div>
                    { !!saveWarning &&
                      <>
                        Warning: changing state definitions or calculated values
                        affects <b>only future</b> values; reporting in past may not represent
                        new values! Acknowlege: 
                        <Checkbox
                          onClick={ () => { setSaveWarning(2) } }
                          checked={ (saveWarning>1) }
                        />
                        <br/>
                      </>
                    }
                    <Button
                      className={classes.actionbutton}
                      color="primary"
                      onClick={saveTagPropertiesToDB}
                      startIcon={<SaveIcon />}
                      disabled={
                        readOnly ||
                        (saveWarning === 1) ||
                        Object.values(errorContext.errors).some(Boolean)
                      }
                    >
                      Save
                    </Button>
                    <Tooltip
                      title="Current changes will be discarded"
                      placement="top"
                    >
                      <Button
                        className={classes.actionbutton}
                        style={{ color: "red" }}
                        onClick={reset}
                      >
                        Cancel
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </Grid>
              <Grid item xs={12}>
                <TagRoleClipboard stack={stack} />
              </Grid>
              <Grid item xs={12}>
                {(selectedTags.length > 0 || newTag) ? (
                  <Tabs
                    value={tab}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="inherit"
                    onChange={(_, newTab) => setTab(newTab)}
                  >
                    {selectedTags.map((tag) => (
                      <Tab
                        key={tag}
                        label={
                          <Box
                            display="flex"
                            alignItems="end"
                            columnGap={4}
                          >
                            <Typography
                              variant="caption"
                              style={{ textTransform: "none" }}
                            >
                              {ISXUtils.getTagName(tag, stack.data_config) || "x"+tag+"X"}
                            </Typography>
                          </Box>
                        }
                        style={{
                          minWidth: 72,
                          paddingLeft: 16,
                          paddingRight: 16,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                      />
                    ))}
                  </Tabs>
                ) : (
                  <Box fontStyle="italic" padding={2.5}>
                    {`Select one or more from the ${tagIds.length} tag${tagIds.length !== 1 ? "s" : ""
                      } of ${stack?.name ||
                      JSON.parse(stack.dev_session)["stack.label"] ||
                      "<Unnamed>"
                      } to view/compare/configure their properties. Or for advanced users:`}
                    <Button
                      className={classes.actionbutton}
                      color="primary"
                      onClick={createNewTag}
                      startIcon={<AddCircle/>}
                    >
                      Create New &nbsp;&nbsp; <div style={{ fontSize: "9px" }}> (use with care!)</div>
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </div>
        </div>
        <Divider />
        <div style={{ height: "100%", overflowY: "auto" }}>
          {(newTag ? [""] : selectedTags).map((tag, idx) => (
            <div
              style={{ paddingLeft: 8, display: idx === tab ? "flex" : "none" }}
              key={tag}
            >
              <TagConfig
                create={newTag && setNewTagId}
                stack={stack}
                tagId={tag}
                useClipboard
                showCheck={false}
                tagProperties={tagProperties[tag]}
                handleTagPropChange={handleTagPropChange}
                handleTagPropsChange={handleTagPropsChange}
                predefined={predefined}
                readOnly={readOnly}
              />
            </div>
          ))}
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            open={saveToastOpen}
            autoHideDuration={3000}
            onClose={closeSaveToast}
            message="Updated tag properties saved"
          />
        </div>
      </div>
    </TagRoleClipboardContextProvider>
  );
};

export default StackTagsConfig;
